import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

let emit = null;
let resolver = null;

// Promise-based, imperative replacement for window.confirm().
// Usage: if (!(await confirm({ title, message, confirmText, tone }))) return;
// With `input: { placeholder }` it resolves to the typed (required) text, or false.
export function confirm(opts) {
  const options = typeof opts === "string" ? { message: opts } : opts || {};
  return new Promise((resolve) => {
    resolver = resolve;
    if (emit) emit(options);
    else resolve(window.confirm(options.message || "Are you sure?"));
  });
}

export function ConfirmHost() {
  const [opts, setOpts] = useState(null);
  const [text, setText] = useState("");

  useEffect(() => {
    emit = (o) => { setText(""); setOpts(o); };
    return () => { emit = null; };
  }, []);

  const close = (val) => {
    if (val && opts?.input) {
      if (!text.trim()) return;
      val = text.trim();
    }
    setOpts(null);
    const r = resolver;
    resolver = null;
    if (r) r(val);
  };

  useEffect(() => {
    if (!opts) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts, text]);

  const danger = !opts || opts.tone !== "neutral";
  const accent = danger ? "#FF6B81" : "#00FF66";

  return createPortal(
    <AnimatePresence>
      {opts && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          data-testid="confirm-overlay"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => close(false)} />
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
            data-testid="confirm-dialog"
          >
            <button
              onClick={() => close(false)}
              className="absolute right-4 top-4 text-white/40 transition-colors hover:text-white"
              data-testid="confirm-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex gap-4 p-6">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${accent}1a`, color: accent }}
              >
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1 pr-6">
                <h3 className="font-display text-lg font-bold tracking-tight text-white" data-testid="confirm-title">
                  {opts.title || "Are you sure?"}
                </h3>
                {opts.message && (
                  <p className="mt-1.5 text-sm leading-relaxed text-white/60" data-testid="confirm-message">
                    {opts.message}
                  </p>
                )}
                {opts.input && (
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={opts.input.placeholder || ""}
                    maxLength={300}
                    autoFocus
                    className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus:border-[#00FF66]/40"
                    data-testid="confirm-input"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2.5 border-t border-white/[0.06] bg-white/[0.02] px-6 py-4">
              <button
                onClick={() => close(false)}
                className="rounded-full border border-white/10 px-4 py-2 text-xs font-bold text-white/70 transition-colors duration-200 hover:bg-white/5 hover:text-white"
                data-testid="confirm-cancel"
              >
                {opts.cancelText || "Cancel"}
              </button>
              <button
                onClick={() => close(true)}
                autoFocus={!opts.input}
                disabled={!!opts.input && !text.trim()}
                className="rounded-full px-4 py-2 text-xs font-bold text-black transition-all duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                style={{ backgroundColor: accent, boxShadow: `0 6px 20px -6px ${accent}` }}
                data-testid="confirm-accept"
              >
                {opts.confirmText || "Confirm"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default ConfirmHost;
